import { CalendarDay } from "../calendar/utils";
import { getPsalmRefFromText } from "../chants/utils";
import { LoadableDataDependency } from "../utils/RemoteDataProvider";
import { LessonData, Settings } from "../utils/settings";
import { getLesserLitany } from "./lesser-litany";
import { getLetUsBlessTheLord } from "./let-us-bless-the-lord";
import { getLordsPrayer } from "./lords-prayer";
import { getOpeningResponses } from "./opening-responses";
import { getOpeningSentence } from "./opening-sentence";
import { getProperCollect } from "./proper-collect";
import { getSalutation } from "./salutation";
import { getSuffrages } from "./suffrages";
import { LiturgyItem } from "./types";

export const getMattinsLiturgy = (
    day: CalendarDay,
    settings: Settings,
    loadablePsalms: LoadableDataDependency<string[]>,
    loadableLessons: LoadableDataDependency<{
        firstLesson: LessonData;
        secondLesson: LessonData;
    }>
) => {
    const items: LiturgyItem[] = [];

    items.push({ type: "heading", day, hour: "mattins" });

    items.push(...getOpeningSentence(day, "mattins"));

    items.push({
        type: "rubric",
        content: "The Officiant may give this **Exhortation.**",
    });
    items.push({
        type: "text",
        content: `Dearly beloved brethren, the Scripture moveth us, in sundry
            places, to acknowledge and confess our manifold sins and
            wickedness; and that we should not dissemble nor cloak them
            before the face of Almighty God our heavenly Father; but confess
            them with an humble, lowly, penitent, and obedient heart; to the
            end that we may obtain forgiveness of the same, by his infinite
            goodness and mercy. And although we ought, at all times, humbly
            to acknowledge our sins before God; yet ought we most chiefly so
            to do, when we assemble and meet together to render thanks for
            the great benefits that we have received at his hands, to set
            forth his most worthy praise, to hear his most holy Word, and to
            ask those things which are requisite and necessary, as well for
            the body as the soul. Wherefore I pray and beseech you, as many
            as are here present, to accompany me with a pure heart, and
            humble voice, unto the throne of the heavenly grace, saying —`,
    });
    items.push({
        type: "rubric",
        content: "All shall kneel and say together the **Confession.**",
        settingsPanelId: "confession",
    });
    items.push({
        type: "text",
        content: `**Almighty and most merciful Father; We have erred, and strayed
            from thy ways like lost sheep. We have followed too much the
            devices and desires of our own hearts. We have offended against
            thy holy laws. We have left undone those things which we ought
            to have done; And we have done those things which we ought not
            to have done; And there is no health in us. But thou, O Lord,
            have mercy upon us, miserable offenders. Spare thou them, O God,
            who confess their faults. Restore thou those who are penitent;
            According to thy promises declared unto mankind in Christ Jesus
            our Lord. And grant, O most merciful Father, for his sake; That
            we may hereafter live a godly, righteous, and sober life, To the
            glory of thy holy Name. Amen.**`,
    });

    if (settings.absolution === "bcp absolution") {
        items.push({
            type: "rubric",
            content:
                "The People still kneeling, the Officiant shall stand and pronounce the **Absolution**.",
        });
        items.push({
            type: "text",
            content: `Almighty God, the Father of our Lord Jesus Christ, who
                desireth not the death of a sinner, but rather that he
                may turn from his wickedness, and live; and hath given
                power, and commandment, to his Ministers, to declare and
                pronounce to his people, being penitent, the Absolution
                and Remission of their sins: He pardoneth and absolveth
                all them that truly repent, and unfeignedly believe his
                holy Gospel. Wherefore let us beseech him to grant us
                true repentance, and his Holy Spirit, that those things
                may please him, which we do at this present; and that
                the rest of our life hereafter may be pure, and holy; so
                that at the last we may come to his eternal joy; through
                Jesus Christ our Lord. **Amen.**`,
        });
    } else if (settings.absolution === "pardon and peace collect") {
        items.push({
            type: "rubric",
            content:
                "All still kneeling, the Officiant shall say this **Collect**.",
        });
        items.push({
            type: "text",
            content: `Grant, we beseech thee, merciful Lord, to thy faithful
                people pardon and peace, that they may be cleansed from
                all their sins, and serve thee with a quiet mind;
                through Jesus Christ our Lord. **Amen.**`,
        });
    }

    if (settings.lordsPrayerAfterConfession === "sung") {
        items.push({
            type: "rubric",
            content: "All kneeling, the **Lord's_Prayer** shall_be_sung.",
        });
        items.push(...getLordsPrayer(false, settings));
    } else if (settings.lordsPrayerAfterConfession === "said") {
        items.push({
            type: "rubric",
            content: "All kneeling, the **Lord's_Prayer** shall_be_said.",
        });
        items.push(...getLordsPrayer(true, settings));
    }

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content:
            "All standing, the Officiant and People shall sing in turn these **Preces_and_Responses.**",
    });

    items.push(...getOpeningResponses(false));

    if (day.easterOctave && settings.paschaNostrum === "octave") {
        items.push({
            type: "rubric",
            content:
                "During the Octave of Easter, the **Pascha_Nostrum** shall_be_sung.",
            settingsPanelId: "invitatory",
        });
        items.push({ type: "canticle", id: "pascha nostrum", day });
    } else if (day.label === "easter-day" && settings.paschaNostrum === "day") {
        items.push({
            type: "rubric",
            content: "On Easter Sunday, the **Pascha_Nostrum** shall_be_sung.",
            settingsPanelId: "invitatory",
        });
        items.push({ type: "canticle", id: "pascha nostrum", day });
    } else {
        items.push({
            type: "rubric",
            content: "Then the **Invitatory_Psalm** shall be sung.",
            settingsPanelId: "invitatory",
        });
        items.push({ type: "canticle", id: "venite", day });
    }

    items.push({ type: "spacer" });

    if (settings.hymnPosition === "before psalms") {
        items.push({
            type: "rubric",
            content: "The **Office Hymn** may be sung.",
            settingsPanelId: "office hymn",
        });

        items.push({ type: "office hymn", day, hour: "mattins" });

        items.push({ type: "spacer" });
    }

    items.push({
        type: "rubric",
        content: `All sitting, the
    **Proper_Psalm${
        loadablePsalms.state === "loaded" && loadablePsalms.value.length === 1
            ? ""
            : "s"
    }**
    shall be sung.`,
        settingsPanelId: "psalms",
    });

    if (loadablePsalms.state === "loaded") {
        loadablePsalms.value.forEach((refText) => {
            items.push({
                type: "psalm",
                psalmRef: getPsalmRefFromText(refText),
            });
        });
    } else {
        items.push({ type: "loading" });
    }

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: "The People sitting, the **First_Lesson** shall_be_read.",
        settingsPanelId: "morning lessons",
    });

    if (
        loadableLessons.state === "loaded" &&
        Array.isArray(loadableLessons.value.firstLesson)
    ) {
        loadableLessons.value.firstLesson.forEach(
            (lessonPart: { title: string; content: any }, index) => {
                items.push({
                    type: "scripture",
                    title: lessonPart.title,
                    content: lessonPart.content,
                    hasBeginneth: index === 0,
                });
            }
        );

        items.push({
            type: "text",
            content: "Here endeth the first lesson.",
        });
    } else {
        items.push({ type: "loading" });
    }

    items.push({ type: "spacer" });

    if (day.antiphonId === "lent") {
        items.push({
            type: "rubric",
            content: "All may stand and sing the **Benedictus_es,_Domine.**",
        });
        items.push({ type: "canticle", id: "benedictus es", day });
        items.push({ type: "spacer" });
    } else {
        items.push({
            type: "rubric",
            content: "All may stand and sing the **Te_Deum.**",
            settingsPanelId: "te deum",
        });
        if (
            settings.teDeum === "always" ||
            (settings.teDeum === "feasts" && day.label !== undefined)
        ) {
            items.push({ type: "canticle", id: "te deum", day });
        }
        items.push({ type: "spacer" });
    }

    items.push({
        type: "rubric",
        content: "The People sitting, the **Second_Lesson** shall_be_read.",
    });

    if (
        loadableLessons.state === "loaded" &&
        Array.isArray(loadableLessons.value.secondLesson)
    ) {
        loadableLessons.value.secondLesson.forEach(
            (lessonPart: { title: string; content: any }, index) => {
                items.push({
                    type: "scripture",
                    title: lessonPart.title,
                    content: lessonPart.content,
                    hasBeginneth: index === 0,
                });
            }
        );

        items.push({
            type: "text",
            content: "Here endeth the second lesson.",
        });
    } else {
        items.push({ type: "loading" });
    }

    items.push({ type: "spacer" });

    if (settings.hymnPosition === "before canticle") {
        items.push({
            type: "rubric",
            content: "The **Office Hymn** may be sung.",
            settingsPanelId: "office hymn",
        });

        items.push({ type: "office hymn", day, hour: "mattins" });

        items.push({ type: "spacer" });
    }

    items.push({
        type: "rubric",
        content: "All standing, the **Gospel_Canticle** shall_be_sung.",
        settingsPanelId: "gospel canticles",
    });
    items.push({ type: "canticle", id: "benedictus", day });

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content:
            "Remaining standing, all shall say together the **Apostles'_Creed.**",
    });
    items.push({
        type: "text",
        content: `**I believe in God, the Father Almighty, Maker of heaven and
            earth; and in Jesus Christ, his only Son our Lord; Who was
            conceived by the Holy Ghost; Born of the Virgin Mary; Suffered
            under Pontius Pilate; Was crucified, dead, and buried; He
            descended into hell; The third day he rose again from the dead;
            He ascended into heaven, And sitteth on the right hand of God
            the Father Almighty; From thence he shall come to judge the
            quick and the dead. I believe in the Holy Ghost; The holy
            Catholic Church; The Communion of Saints; The Forgiveness of
            sins; The Resurrection of the body; And the life everlasting.
            Amen.**`,
    });

    items.push({ type: "spacer" });

    items.push({ type: "litany rubric", day });

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content:
            "The Officiant shall sing the **Salutation**, the_People_responding.",
        settingsPanelId: "salutation",
    });

    items.push(...getSalutation(settings));

    items.push({
        type: "rubric",
        content:
            "All kneeling, the Officiant and People shall sing the **Lesser_Litany.**",
    });
    items.push(...getLesserLitany());

    items.push({
        type: "rubric",
        content: "All shall sing together the **Lord's_Prayer.**",
        settingsPanelId: "lords prayer",
    });
    items.push(...getLordsPrayer(false, settings));

    items.push({
        type: "rubric",
        content:
            "The Officiant and People shall sing in turn the **Suffrages.**",
    });
    items.push(...getSuffrages(settings));
    items.push({ type: "spacer" });

    items.push(...getProperCollect(day, "mattins", false, settings));

    items.push({ type: "rubric", content: "The **Collect_for_Peace,**" });
    items.push({
        type: "text",
        content: `O God, who art the author of peace and lover of concord, in
            knowledge of whom standeth our eternal life, whose service is
            perfect freedom; Defend us thy humble servants in all assaults
            of our enemies; that we, surely trusting in thy defence, may not
            fear the power of any adversaries, through the might of Jesus
            Christ our Lord. **Amen.**`,
    });

    items.push({ type: "rubric", content: "And the **Collect_for_Grace.**" });
    items.push({
        type: "text",
        content: `O Lord, our heavenly Father, Almighty and everlasting God, who
            hast safely brought us to the beginning of this day; Defend us
            in the same with thy mighty power; and grant that this day we
            fall into no sin, neither run into any kind of danger; but that
            all our doings may be ordered by thy governance, to do always
            that which is righteous in thy sight; through Jesus Christ our
            Lord. **Amen.**`,
    });

    if (settings.letUsBlessMattinsAndEvensong === "after collects") {
        items.push({ type: "spacer" });
        items.push({
            type: "rubric",
            content: "The Officiant shall sing, the_People_responding,",
            settingsPanelId: "let us bless",
        });
        items.push(...getLetUsBlessTheLord(day, "mattins"));
        items.push({ type: "spacer" });
    }

    items.push({
        type: "rubric",
        content: "All may say together this **General_Thanksgiving.**",
    });
    items.push({
        type: "text",
        content: `**Almighty God, Father of all mercies, we thine unworthy servants
            do give thee most humble and hearty thanks for all thy goodness
            and loving-kindness to us, and to all men. We bless thee for our
            creation, preservation, and all the blessings of this life; but
            above all, for thine inestimable love in the redemption of the
            world by our Lord Jesus Christ; for the means of grace, and for
            the hope of glory. And, we beseech thee, give us that due sense
            of all thy mercies, that our hearts may be unfeignedly thankful,
            and that we may show forth thy praise, not only with our lips,
            but in our lives; by giving up ourselves to thy service, and by
            walking before thee in holiness and righteousness all our days;
            through Jesus Christ our Lord, to whom with thee and the Holy
            Ghost be all honour and glory, world without end. Amen.**`,
    });

    items.push({
        type: "rubric",
        content:
            "The Officiant may say this **Prayer_of_Saint_John_Chrysostom.**",
    });
    items.push({
        type: "text",
        content: `Almighty God, who hast given us grace at this time with one
            accord to make our common supplications unto thee; and dost
            promise, that when two or three are gathered together in thy
            Name thou wilt grant their requests: Fulfil now, O Lord, the
            desires and petitions of thy servants, as may be most expedient
            for them; granting us in this world knowledge of thy truth, and
            in the world to come life everlasting. **Amen.**`,
    });

    if (settings.hymnPosition === "anthem") {
        items.push({
            type: "rubric",
            content: "The **Office Hymn** may be sung.",
            settingsPanelId: "office hymn",
        });

        items.push({ type: "office hymn", day, hour: "mattins" });

        items.push({ type: "spacer" });
    }

    if (settings.letUsBlessMattinsAndEvensong === "before grace") {
        items.push({ type: "spacer" });
        items.push({
            type: "rubric",
            content: "The Officiant shall sing, the_People_responding,",
            settingsPanelId: "let us bless",
        });
        items.push(...getLetUsBlessTheLord(day, "mattins"));
        items.push({ type: "spacer" });
    }

    items.push({
        type: "rubric",
        content: "The Officiant shall say **The_Grace.**",
    });
    items.push({
        type: "text",
        content: `The grace of our Lord Jesus Christ, and the love of God, and the
            fellowship of the Holy Ghost, be with us all evermore. **Amen.**`,
    });

    items.push({ type: "spacer", big: true });

    items.push({ type: "footer", day, hour: "mattins" });

    return items;
};
